import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import { currentYear } from "../../../utils";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import {
  trackFlightDetailsPrint,
  trackPageView,
} from "../../analytics/AnalyticsActions";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
  changeSelectedFlight,
  selectSelectedFlight,
} from "../searchFlightsSlice";
import { RMAnalytics } from "../../analytics/RMAnalytics";
import SearchFlightsResultModalDetails from "./SearchFlightsResultModalDetails";
import SearchFlightsResultModalStatus from "./SearchFlightsResultModalStatus";
import { selectConfig } from "../../config/ConfigSlice";
import BookingButton from "../BookingButton";

function SearchFlightsResultModal() {
  const dispatch = useAppDispatch();
  const result = useAppSelector(selectSelectedFlight);
  const { mapper, components } = useAppSelector(selectConfig);
  const { t } = useTranslation();
  const printRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [currentTab, setCurrentTab] = useState<number>(1);

  const scrollToModal = useCallback((node: HTMLElement) => {
    if (node !== null) {
      setTimeout(() => {
        document.body.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }, 100);
    }
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: "margin: 20px",
    onBeforePrint: async () => {
      dispatch(trackFlightDetailsPrint());
    },
  });

  const closeModal = () => {
    dispatch(changeSelectedFlight(null));
    setCurrentTab(1);
    dispatch(trackPageView(RMAnalytics.PageView.SEARCH_RESULTS));
  };

  useEffect(() => {
    if (result) {
      // focus the close button when modal opens
      setTimeout(() => closeButtonRef.current?.focus(), 0);
    }
  }, [result]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && result) {
        closeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [result]);

  if (result) {
    return (
      <div
        id="flight-details-modal"
        className={"modal is-active"}
        role="dialog"
        aria-modal="true"
        aria-label={t("FLIGHT_DETAILS")}
      >
        <div className="modal-background" onClick={closeModal} />
        <div
          id="flight-details-modal-card"
          className="modal-card"
          ref={scrollToModal}
        >
          <header className="modal-card-head">
            <p className="modal-card-title">{t("FLIGHT_DETAILS")}</p>
            <button
              ref={closeButtonRef}
              className="delete"
              aria-label="close"
              onClick={closeModal}
            />
          </header>
          <section className="modal-card-body">
            <div ref={printRef}>
              {components.Results.showFlightStatusInPopup && (
                <div className="tabs">
                  <ul>
                    <li className={currentTab === 1 ? "is-active" : ""}>
                      <a href="#" onClick={() => setCurrentTab(1)}>
                        Details
                      </a>
                    </li>
                    <li className={currentTab === 2 ? "is-active" : ""}>
                      <a href="#" onClick={() => setCurrentTab(2)}>
                        Status
                      </a>
                    </li>
                  </ul>
                </div>
              )}
              {currentTab === 1 && (
                <SearchFlightsResultModalDetails
                  result={result}
                  mapper={mapper}
                />
              )}
              {currentTab === 2 && (
                <SearchFlightsResultModalStatus result={result} />
              )}
              <span className="is-size-7">{`© ${currentYear} ${t(
                "OAG_DISCLAIMER"
              )}`}</span>
            </div>
          </section>
          <footer className="modal-card-foot" id="modal-buttons">
            <button
              id="print-page-button"
              className="button is-small is-primary"
              onClick={handlePrint}
            >
              <span className="icon">
                <FontAwesomeIcon icon={faPrint} />
              </span>
              <span>{t("BUTTON_PRINT_PAGE")}</span>
            </button>
            {mapper.bookingLink.url && (
              <div className="control">
                <BookingButton />
              </div>
            )}
          </footer>
        </div>
      </div>
    );
  } else {
    return null;
  }
}

export default SearchFlightsResultModal;
